import {
  EmployeeJobTitle,
  FEATURE_FLAGS,
  HexColor,
  IconNames,
  Shop,
  ShopConfig,
  TerminologyOptions,
  TimeOption,
} from "@/types/v2";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/Base/Form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Tooltip } from "@/components/Common/Tooltip";
import {
  DEFAULT_COMPLETED_ON_TIME_BUFFER_MIN,
  DEFAULT_CUSTOMER_UPDATE_SNOOZE_MIN,
  DEFAULT_DASHBOARD_SCALING,
  DEFAULT_SHOW_CONTAINER_LINK_ICONS,
  DEFAULT_SHOW_ADDITIONAL_CARD_TECHNICIANS,
  DEFAULT_SHOW_CARD_TECHNICIAN,
  DEFAULT_SHOW_LABELS_AS_TAGS,
  DEFAULT_TECHNICIAN_WRITEBACK_ENABLED,
  DEFAULT_USE_SHOPHERO_EMPLOYEE_JOB_TITLE,
  DEFAULT_INBOX_PANEL_CONFIG,
  PROMISE_TIME_PAST_COLOR,
} from "@/lib/const";
import { Switch } from "@/components/Base/Switch";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { useEmployees } from "@/hooks/shop/useEmployees";
import { FeatureFlagged } from "@/components/Common/FeatureFlagged";
import { Icon } from "@/components/Common/Icon";
import { Input } from "@/components/Base/Input";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/Base/Card";
import { ShopConfiguration } from "@/components/Settings/Shop/ShopConfiguration";
import { ColorPicker } from "@/components/Dashboard/components/ColorPicker/ColorPicker";
import { DEFAULT_TIME_OPTIONS } from "@/components/CustomerUpdate/TimeUntilSelector";
import { Button } from "@/components/Base/Button";
import { Label } from "@/components/Base/Label";
import {
  shopConfigSchema,
  ShopConfigFromZod,
} from "@/components/Settings/Shop/shopConfigSchema";
import {
  detectBrowserTimeZone,
  SHOP_TIME_ZONE_OPTIONS,
} from "@/lib/shopTimeZone";

const defaultValues: ShopConfig = {
  dashboardScaling: DEFAULT_DASHBOARD_SCALING,
  useShopHeroEmployeeJobTitle: DEFAULT_USE_SHOPHERO_EMPLOYEE_JOB_TITLE,
  showContainerLinkIcons: DEFAULT_SHOW_CONTAINER_LINK_ICONS,
  // Must be defaulted: a registered field with no default submits as
  // `undefined`, which Firestore rejects on this write path.
  showCardTechnician: DEFAULT_SHOW_CARD_TECHNICIAN,
  showAdditionalCardTechnicians: DEFAULT_SHOW_ADDITIONAL_CARD_TECHNICIANS,
  showLabelsAsTags: DEFAULT_SHOW_LABELS_AS_TAGS,
  customerUpdates: {
    enabled: false,
    defaultCompletedOnTimeBufferMin: DEFAULT_COMPLETED_ON_TIME_BUFFER_MIN, // Default buffer in minutes for customer updates to be considered completed on time
    defaultSnoozeBufferMin: DEFAULT_CUSTOMER_UPDATE_SNOOZE_MIN, // Default snooze buffer in minutes
    requireComments: false,
    autoCreateCustomerUpdates: true, // Whether to automatically create customer updates when a card is created
    quickTimeOptions: DEFAULT_TIME_OPTIONS,
  },
  inboxPanel: DEFAULT_INBOX_PANEL_CONFIG,
};

type ShopSettingsFormProps = {
  shop?: Shop;
  onSubmit?: (values: ShopConfig) => void;
  onFeatureToggle?: (feature: FEATURE_FLAGS, enabled: boolean) => void;
};

export const ShopSettingsForm = ({
  shop,
  onSubmit,
  onFeatureToggle,
}: ShopSettingsFormProps) => {
  const authEmployees = useEmployees(true);
  const serviceAdvisors = authEmployees.filter(
    (e) => e.shopHeroJobTitle === EmployeeJobTitle.SERVICE_ADVISOR
  );
  const { terminolize } = useTerminologyReplacer();
  // Display-only hint for the Time Zone field below — never written to the
  // form or submitted. See the comment on the form's timeZone default value
  // for why the browser-detected zone must not be the value itself.
  const detectedTimeZone = useMemo(() => detectBrowserTimeZone(), []);
  // Never let the form carry workingHours — ShopConfiguration owns them, and a
  // stale copy here would revert edits on the next unrelated form submit (A).
  const { workingHours, ...shopConfigWithoutHours } = shop?.shopConfig ?? {};
  const form = useForm<ShopConfigFromZod>({
    resolver: zodResolver(shopConfigSchema),
    defaultValues: {
      ...defaultValues,
      ...shopConfigWithoutHours,
      customerUpdates: {
        ...defaultValues.customerUpdates,
        ...shop?.shopConfig?.customerUpdates,
      },
      inboxPanel: {
        ...defaultValues.inboxPanel,
        ...shop?.shopConfig?.inboxPanel,
      },
      promiseTime: {
        ...shop?.shopConfig?.promiseTime,
        pastDueColor:
          shop?.shopConfig?.promiseTime?.pastDueColor ??
          PROMISE_TIME_PAST_COLOR,
      },
      // `timeZone` is deliberately NOT defaulted here — it comes from the
      // `...shopConfigWithoutHours` spread above when the shop has one stored,
      // and is otherwise ABSENT. Every handler in this component resubmits the
      // whole form on any unrelated field edit, so any default at all gets
      // silently persisted the first time someone toggles an unrelated switch.
      // Persisting a fabricated zone is the failure mode the rollout cannot
      // tolerate: `getShopTimeZone` only warns when the zone is absent or
      // invalid, and that warning is the deploy plan's only signal that a shop
      // is sweeping under the wrong zone. A stamped "America/Chicago" is
      // indistinguishable from a deliberate one, so a Los Angeles shop would
      // sweep at 6:00 AM local forever with clean logs. The empty Select shows
      // its placeholder and the "Your browser is in X" hint below it, which is
      // the correct affordance — and only means anything while the field is
      // genuinely empty.
    },
  });

  const customerUpdatesEnabled = form.watch("customerUpdates.enabled");

  const [saveState, setSaveState] = useState<"idle" | "editing" | "saved">(
    "idle"
  );
  const saveIndicatorTimeoutRef = useRef<NodeJS.Timeout>();

  const handleSubmit = (values: ShopConfigFromZod) => {
    if (onSubmit) {
      // An unset time zone must be OMITTED, not sent as `undefined`. Firestore
      // is not configured with `ignoreUndefinedProperties` on this path, and
      // CompanyInfo.page.tsx merges with `{ ...shopData?.shopConfig,
      // ...update.shopConfig }` — so a present-but-undefined key would spread
      // over and clobber an already-correct stored zone, not merely fail to set
      // one. Stripped here rather than reasoned about through react-hook-form's
      // registration internals.
      const { timeZone, ...rest } = values;
      const timeZonePayload = timeZone ? { ...rest, timeZone } : rest;

      // `technicianWritebackEnabled`'s Controller (Technician Sync Card) can
      // mount with no `defaultValues` entry whenever
      // FEATURE_FLAGS.isTechnicianWritebackEnabled is on for the shop — i.e.
      // exactly the shops this feature is rolled out to. react-hook-form
      // then registers the field as `undefined` until the admin actually
      // touches the switch, and an unrelated submit must not let that
      // `undefined` become an own property on the payload: unlike
      // `timeZone`, an admin can deliberately set this to `false`, so the
      // strip has to key on `=== undefined` rather than falsiness. The
      // clobber risk is identical to `timeZone` above — CompanyInfo.page.tsx
      // merges with `{ ...shopData?.shopConfig, ...update.shopConfig }`, and
      // Firestore has no `ignoreUndefinedProperties` on this path — a
      // present-but-undefined key would spread over and clobber an
      // already-stored `true`.
      const { technicianWritebackEnabled, ...restWithoutWriteback } =
        timeZonePayload;
      const payload = (technicianWritebackEnabled === undefined
        ? restWithoutWriteback
        : {
            ...restWithoutWriteback,
            technicianWritebackEnabled,
          }) as unknown as ShopConfig;
      onSubmit(payload);
      setSaveState("saved");

      if (saveIndicatorTimeoutRef.current) {
        clearTimeout(saveIndicatorTimeoutRef.current);
      }

      saveIndicatorTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 2000);
    }
  };

  const handleSelectChange = (
    fieldName: "dashboardScaling" | "timeZone",
    fieldValue: string
  ) => {
    switch (fieldName) {
      case "dashboardScaling":
        form.setValue(
          "dashboardScaling",
          fieldValue as ShopConfigFromZod["dashboardScaling"]
        );
        break;
      case "timeZone":
        form.setValue("timeZone", fieldValue);
        break;
    }
    handleSubmit(form.getValues());
  };

  const handleSwitchChange = (
    fieldValue: boolean,
    fieldName: keyof ShopConfigFromZod
  ) => {
    form.setValue(fieldName, fieldValue);
    handleSubmit(form.getValues());
  };

  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  const debouncedSubmit = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      handleSubmit(form.getValues());
    }, 500);
  }, [form]);

  const handleInputChange = useCallback(
    (value: number, fieldName: string) => {
      // @ts-expect-error Value is a number, and is compatible with the form field type
      form.setValue(fieldName as keyof ShopConfigFromZod, value);
      setSaveState("editing");
      debouncedSubmit();
    },
    [form, debouncedSubmit]
  );

  const handleInputBlur = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    handleSubmit(form.getValues());
  }, [form]);

  // Quick time options management
  const [quickTimeOptions, setQuickTimeOptions] = useState<TimeOption[]>(
    shop?.shopConfig?.customerUpdates?.quickTimeOptions || DEFAULT_TIME_OPTIONS
  );

  const handleQuickTimeOptionChange = (
    index: number,
    field: "label" | "value",
    newValue: string | number
  ) => {
    const updatedOptions = [...quickTimeOptions];
    if (field === "label") {
      updatedOptions[index].label = newValue as string;
    } else {
      updatedOptions[index].value = Number(newValue);
    }
    setQuickTimeOptions(updatedOptions);
    form.setValue("customerUpdates.quickTimeOptions", updatedOptions);
    setSaveState("editing");
    debouncedSubmit();
  };

  const handleAddQuickTimeOption = () => {
    const newOptions = [...quickTimeOptions, { label: "1h", value: 60 }];
    setQuickTimeOptions(newOptions);
    form.setValue("customerUpdates.quickTimeOptions", newOptions);
    handleSubmit(form.getValues());
  };

  const handleRemoveQuickTimeOption = (index: number) => {
    if (quickTimeOptions.length <= 1) return; // Keep at least one option
    const newOptions = quickTimeOptions.filter((_, i) => i !== index);
    setQuickTimeOptions(newOptions);
    form.setValue("customerUpdates.quickTimeOptions", newOptions);
    handleSubmit(form.getValues());
  };

  const handleResetQuickTimeOptions = () => {
    setQuickTimeOptions(DEFAULT_TIME_OPTIONS);
    form.setValue("customerUpdates.quickTimeOptions", DEFAULT_TIME_OPTIONS);
    handleSubmit(form.getValues());
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (saveIndicatorTimeoutRef.current) {
        clearTimeout(saveIndicatorTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Form {...form}>
      <div className="relative">
        {saveState !== "idle" && (
          <div className="absolute right-0 top-0 z-10 flex h-8 w-8 items-center justify-center rounded-full border bg-background shadow-sm transition-all duration-300 ease-in-out">
            {saveState === "editing" && (
              <Icon
                name={IconNames.editLine}
                className="h-4 w-4 animate-pulse text-muted-foreground"
              />
            )}
            {saveState === "saved" && (
              <Icon
                name={IconNames.check}
                className="h-4 w-4 text-success duration-300 animate-in zoom-in-50"
              />
            )}
          </div>
        )}
        <form
          className="min-w-64 flex-grow space-y-4"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <h1 className={"font-medium"}>Shop Settings</h1>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                name={"dashboardScaling"}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dashboard Scaling</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(value) =>
                          handleSelectChange("dashboardScaling", value)
                        }
                        defaultValue={"basic"}
                        value={field.value}
                      >
                        <SelectTrigger className={"w-64"}>
                          <SelectValue
                            placeholder={"Choose a dashboard scaling option"}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={"noScaling"}>
                            <div
                              className={
                                "flex w-48 flex-row items-center justify-between"
                              }
                            >
                              <div>No Scaling</div>
                              <Tooltip
                                message={
                                  "The layout will not scale to fit the screen. This is the legacy setting."
                                }
                              >
                                <Icon name={IconNames.info} />
                              </Tooltip>
                            </div>
                          </SelectItem>
                          <SelectItem value={"scaleToFit"}>
                            <div
                              className={
                                "flex w-48 flex-row items-center justify-between"
                              }
                            >
                              <div>Scale to Fit</div>
                              <Tooltip
                                message={
                                  "The layout will scale to fit the screen. Everything in the layout will fit within the screen and will scale accordingly."
                                }
                              >
                                <Icon name={IconNames.info} />
                              </Tooltip>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                name={"timeZone"}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time Zone</FormLabel>
                    <FormDescription>
                      Used to interpret scheduled automations.
                    </FormDescription>
                    <FormControl>
                      <Select
                        onValueChange={(value) =>
                          handleSelectChange("timeZone", value)
                        }
                        // `?? ""` keeps the control CONTROLLED while the field
                        // is unset. Radix treats "" and undefined alike for
                        // placeholder purposes, but passing undefined would
                        // make the Select uncontrolled until the first pick.
                        value={field.value ?? ""}
                      >
                        <SelectTrigger className={"w-64"}>
                          <SelectValue placeholder={"Select a time zone"} />
                        </SelectTrigger>
                        <SelectContent>
                          {SHOP_TIME_ZONE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    {!shop?.shopConfig?.timeZone &&
                      detectedTimeZone !== field.value && (
                        <FormDescription className="text-gray-600">
                          Your browser is in {detectedTimeZone}.
                        </FormDescription>
                      )}
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                name={"useShopHeroEmployeeJobTitle"}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Use Shop Hero Employee Job Title
                      </FormLabel>
                      <FormDescription>
                        Use the employee&#39;s job title from Shop Hero to
                        separate employees into{" "}
                        {terminolize(
                          "Service Advisors",
                          TerminologyOptions.serviceAdvisor,
                          "plural"
                        )}{" "}
                        and{" "}
                        {terminolize(
                          "Technicians",
                          TerminologyOptions.technician,
                          "plural"
                        )}{" "}
                        in the filters other lists.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value ?? true}
                        onCheckedChange={(value) =>
                          handleSwitchChange(
                            value,
                            field.name as keyof ShopConfigFromZod
                          )
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                name={"showContainerLinkIcons"}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Show Container Link Icons
                      </FormLabel>
                      <FormDescription>
                        Show the linked label and linked{" "}
                        {terminolize(
                          "technician",
                          TerminologyOptions.technician,
                          "singular"
                        )}{" "}
                        icons on container headers on the dashboard. These links
                        are always shown in the Layout Builder, where they are
                        configured.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={
                          field.value ?? DEFAULT_SHOW_CONTAINER_LINK_ICONS
                        }
                        onCheckedChange={(value) =>
                          handleSwitchChange(
                            value,
                            field.name as keyof ShopConfigFromZod
                          )
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                name={"showLabelsAsTags"}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Show Tekmetric Labels as Tags
                      </FormLabel>
                      <FormDescription>
                        Show each card&apos;s synced DMS label inline with its
                        other tags on the board. A per-card override in the
                        card&apos;s tags editor takes precedence over this
                        default.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value ?? DEFAULT_SHOW_LABELS_AS_TAGS}
                        onCheckedChange={(value) =>
                          handleSwitchChange(
                            value,
                            field.name as keyof ShopConfigFromZod
                          )
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                name={"showCardTechnician"}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Show Technician on Cards
                      </FormLabel>
                      <FormDescription>
                        Show the{" "}
                        {terminolize(
                          "technician",
                          TerminologyOptions.technician,
                          "singular"
                        )}{" "}
                        initials badge on each card on the dashboard. Turning
                        this off also hides the service advisor popover the
                        badge opens.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value ?? DEFAULT_SHOW_CARD_TECHNICIAN}
                        onCheckedChange={(value) =>
                          handleSwitchChange(
                            value,
                            field.name as keyof ShopConfigFromZod
                          )
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                name={"showAdditionalCardTechnicians"}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Show Additional Technicians on Cards
                      </FormLabel>
                      <FormDescription>
                        Show every{" "}
                        {terminolize(
                          "technician",
                          TerminologyOptions.technician,
                          "singular"
                        )}{" "}
                        on a card. When off, each card shows one: the RO&apos;s
                        assigned{" "}
                        {terminolize(
                          "technician",
                          TerminologyOptions.technician,
                          "singular"
                        )}{" "}
                        or, if none, the first job&apos;s. Requires Show
                        Technician on Cards.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={
                          field.value ??
                          DEFAULT_SHOW_ADDITIONAL_CARD_TECHNICIANS
                        }
                        disabled={
                          !(
                            form.watch("showCardTechnician") ??
                            DEFAULT_SHOW_CARD_TECHNICIAN
                          )
                        }
                        onCheckedChange={(value) =>
                          handleSwitchChange(
                            value,
                            field.name as keyof ShopConfigFromZod
                          )
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                <ShopConfiguration />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FeatureFlagged feature={FEATURE_FLAGS.isCustomerUpdatesEnabled}>
                <Card>
                  <CardHeader>Customer Updates</CardHeader>
                  <CardDescription className="px-6 pb-4">
                    Customer updates are reminders to update a customer on the
                    status of the{" "}
                    {terminolize(
                      "RO",
                      TerminologyOptions.repairOrder,
                      "acronym"
                    )}
                    . They will appear on the dashboard and can be assigned to{" "}
                    {terminolize(
                      "Service Advisors",
                      TerminologyOptions.serviceAdvisor,
                      "plural"
                    )}{" "}
                    to complete.
                  </CardDescription>
                  <CardContent>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row gap-4">
                        <FormField
                          name={"customerUpdates.enabled"}
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                              <div className="space-y-0.5">
                                <FormLabel>Customer Updates Enabled</FormLabel>
                                <FormDescription className="text-gray-600">
                                  Enable or disable customer updates for this
                                  shop. When enabled, service advisors are
                                  prompted to create and complete customer
                                  updates.
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value ?? true}
                                  onCheckedChange={(value) =>
                                    handleSwitchChange(
                                      value,
                                      field.name as keyof ShopConfigFromZod
                                    )
                                  }
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        {serviceAdvisors.length === 0 &&
                          form.getValues().customerUpdates?.enabled && (
                            <div className="flex-col items-center justify-center gap-2 rounded-lg border border-destructive bg-destructive/20 p-4 text-center">
                              <div className="flex items-center justify-center">
                                <Icon name={IconNames.warning} size={24} />
                              </div>
                              <span>
                                You have no available{" "}
                                {terminolize(
                                  "service advisors",
                                  TerminologyOptions.serviceAdvisor,
                                  "plural"
                                )}{" "}
                                to assign customer updates. Make sure you have
                                at least one{" "}
                                {terminolize(
                                  "service advisor",
                                  TerminologyOptions.serviceAdvisor,
                                  "singular"
                                )}{" "}
                                with a Shop Hero account and the job title set
                                to{" "}
                                {terminolize(
                                  "Service Advisor",
                                  TerminologyOptions.serviceAdvisor,
                                  "singular"
                                )}
                              </span>
                            </div>
                          )}
                      </div>
                      {customerUpdatesEnabled && (
                        <div className="flex flex-col gap-2">
                          <FormField
                            name={
                              "customerUpdates.defaultCompletedOnTimeBufferMin"
                            }
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                                <div className="space-y-0.5">
                                  <FormLabel>
                                    Default Completed On Time Buffer (minutes)
                                  </FormLabel>
                                  <FormDescription className="text-gray-600">
                                    The default buffer in minutes for customer
                                    updates to be considered completed on time.
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Input
                                    type="number"
                                    className="w-24"
                                    value={field.value}
                                    onChange={(e) => {
                                      const value = Number(e.target.value);
                                      field.onChange(value);
                                      handleInputChange(value, field.name);
                                    }}
                                    onBlur={handleInputBlur}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            name={"customerUpdates.defaultSnoozeBufferMin"}
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                                <div className="space-y-0.5">
                                  <FormLabel>
                                    Default Snooze Buffer (minutes)
                                  </FormLabel>
                                  <FormDescription className="text-gray-600">
                                    The default snooze buffer in minutes for
                                    customer updates.
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Input
                                    type="number"
                                    className="w-24"
                                    value={field.value}
                                    onChange={(e) => {
                                      const value = Number(e.target.value);
                                      field.onChange(value);
                                      handleInputChange(value, field.name);
                                    }}
                                    onBlur={handleInputBlur}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            name={"customerUpdates.requireComments"}
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                                <div className="space-y-0.5">
                                  <FormLabel>Require Comments</FormLabel>
                                  <FormDescription className="text-gray-600">
                                    When enabled, service advisors will be
                                    required to add comments when completing and
                                    skipping customer updates.
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value ?? true}
                                    onCheckedChange={(value) =>
                                      handleSwitchChange(
                                        value,
                                        field.name as keyof ShopConfigFromZod
                                      )
                                    }
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            name={"customerUpdates.autoCreateCustomerUpdates"}
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                                <div className="space-y-0.5">
                                  <FormLabel>
                                    Automatically Create Customer Updates
                                  </FormLabel>
                                  <FormDescription className="text-gray-600">
                                    When enabled, customer updates will be
                                    automatically created (but not scheduled)
                                    when a card is created. When disabled,
                                    customer updates can only be created
                                    manually or through automations.
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value ?? true}
                                    onCheckedChange={(value) =>
                                      handleSwitchChange(
                                        value,
                                        field.name as keyof ShopConfigFromZod
                                      )
                                    }
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <div className="rounded-lg border p-4">
                            <div className="mb-4 flex items-center justify-between">
                              <div className="space-y-0.5">
                                <FormLabel>Quick Time Options</FormLabel>
                                <FormDescription className="text-gray-600">
                                  Configure the quick select time options
                                  displayed when creating customer updates. The
                                  first option will be selected by default.
                                </FormDescription>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleResetQuickTimeOptions}
                              >
                                Reset to Default
                              </Button>
                            </div>
                            <div className="space-y-2">
                              <div className="grid grid-cols-3 items-center gap-2">
                                <Label>Label</Label>
                                <Label>Minutes</Label>
                              </div>
                              {quickTimeOptions.map((option, index) => (
                                <div
                                  key={index}
                                  className="grid grid-cols-3 items-center gap-2"
                                >
                                  <Input
                                    type="text"
                                    placeholder="Label (e.g., 30m)"
                                    value={option.label}
                                    onChange={(e) =>
                                      handleQuickTimeOptionChange(
                                        index,
                                        "label",
                                        e.target.value
                                      )
                                    }
                                    onBlur={handleInputBlur}
                                  />
                                  <Input
                                    type="number"
                                    placeholder="Minutes"
                                    value={option.value}
                                    onChange={(e) =>
                                      handleQuickTimeOptionChange(
                                        index,
                                        "value",
                                        e.target.value
                                      )
                                    }
                                    onBlur={handleInputBlur}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="w-fit justify-self-end"
                                    onClick={() =>
                                      handleRemoveQuickTimeOption(index)
                                    }
                                    disabled={quickTimeOptions.length <= 1}
                                  >
                                    <Icon name={IconNames.delete} />
                                  </Button>
                                </div>
                              ))}

                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleAddQuickTimeOption}
                                className="mt-2"
                              >
                                <Icon name={IconNames.add} className="mr-2" />
                                Add Option
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </FeatureFlagged>
              <FeatureFlagged
                feature={FEATURE_FLAGS.isTechnicianWritebackEnabled}
              >
                <Card>
                  <CardHeader>Technician Sync</CardHeader>
                  <CardDescription className="px-6 pb-4">
                    Sync a card&#39;s assigned technician back to Tekmetric when
                    it&#39;s moved into a technician-linked container.
                  </CardDescription>
                  <CardContent>
                    <FormField
                      name={"technicianWritebackEnabled"}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">
                              Sync technician assignment to Tekmetric
                            </FormLabel>
                            <FormDescription>
                              When enabled, moving a card into a
                              technician-linked container writes the technician
                              back to the repair order in Tekmetric.
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={
                                field.value ??
                                DEFAULT_TECHNICIAN_WRITEBACK_ENABLED
                              }
                              onCheckedChange={(value) =>
                                handleSwitchChange(
                                  value,
                                  field.name as keyof ShopConfigFromZod
                                )
                              }
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </FeatureFlagged>
              <div className="flex flex-col gap-4">
                <Card>
                  <CardHeader>Inbox Panel</CardHeader>
                  <CardDescription className="px-6 pb-4">
                    Configure which tabs are displayed in the inbox panel. If
                    all tabs are disabled, the inbox panel will be hidden
                    entirely.
                  </CardDescription>
                  <CardContent>
                    <div className="flex flex-col gap-2">
                      <FormField
                        name={"inboxPanel.showActivityFeed"}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel>Show Activity Feed Tab</FormLabel>
                              <FormDescription className="text-gray-600">
                                Display the Activity Feed tab in the inbox
                                panel.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value ?? true}
                                onCheckedChange={(value) =>
                                  handleSwitchChange(
                                    value,
                                    field.name as keyof ShopConfigFromZod
                                  )
                                }
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        name={"inboxPanel.showMessages"}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel>Show Message Board Tab</FormLabel>
                              <FormDescription className="text-gray-600">
                                Display the Message Board tab in the inbox
                                panel.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value ?? true}
                                onCheckedChange={(value) =>
                                  handleSwitchChange(
                                    value,
                                    field.name as keyof ShopConfigFromZod
                                  )
                                }
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        name={"inboxPanel.showGoList"}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel>Show Go List Tab</FormLabel>
                              <FormDescription className="text-gray-600">
                                Display the Go List tab in the inbox panel.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value ?? true}
                                onCheckedChange={(value) =>
                                  handleSwitchChange(
                                    value,
                                    field.name as keyof ShopConfigFromZod
                                  )
                                }
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FeatureFlagged
                        feature={FEATURE_FLAGS.isCustomerUpdatesEnabled}
                      >
                        <FormField
                          name={"inboxPanel.showCustomerUpdates"}
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                              <div className="space-y-0.5">
                                <FormLabel>Show Customer Updates Tab</FormLabel>
                                <FormDescription className="text-gray-600">
                                  Display the Customer Updates tab in the inbox
                                  panel.
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value ?? true}
                                  onCheckedChange={(value) =>
                                    handleSwitchChange(
                                      value,
                                      field.name as keyof ShopConfigFromZod
                                    )
                                  }
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </FeatureFlagged>
                      <FeatureFlagged
                        feature={FEATURE_FLAGS.isDispatchViewEnabled}
                      >
                        <FormField
                          name={"inboxPanel.showDispatchView"}
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                              <div className="space-y-0.5">
                                <FormLabel>Show Dispatch View Tab</FormLabel>
                                <FormDescription className="text-gray-600">
                                  Display the dispatch view tab in the inbox
                                  panel.
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value ?? true}
                                  onCheckedChange={(value) =>
                                    handleSwitchChange(
                                      value,
                                      field.name as keyof ShopConfigFromZod
                                    )
                                  }
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </FeatureFlagged>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>Promise Time</CardHeader>
                  <CardDescription className="px-6 pb-4">
                    Configure how cards appear when their promise time is past
                    due.
                  </CardDescription>
                  <CardContent>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel>Enable Promise Time</FormLabel>
                          <FormDescription className="text-gray-600">
                            Show past-due card highlights based on a card's
                            promise time.
                          </FormDescription>
                        </div>
                        <Switch
                          checked={
                            shop?.enabledFeatures?.isPromiseTimeEnabled ?? false
                          }
                          onCheckedChange={(value) =>
                            onFeatureToggle?.(
                              FEATURE_FLAGS.isPromiseTimeEnabled,
                              value
                            )
                          }
                        />
                      </div>
                      {shop?.enabledFeatures?.isPromiseTimeEnabled && (
                        <div className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel>Past Due Card Color</FormLabel>
                            <FormDescription className="text-gray-600">
                              The color applied to a card when its promise time
                              has passed.
                            </FormDescription>
                          </div>
                          <ColorPicker
                            color={
                              (form.watch(
                                "promiseTime.pastDueColor"
                              ) as HexColor) ?? PROMISE_TIME_PAST_COLOR
                            }
                            onColorChange={(color) => {
                              form.setValue("promiseTime.pastDueColor", color);
                              handleSubmit(form.getValues());
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Form>
  );
};
